import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'

export interface FileInfo {
  name: string
  displayName: string
  size?: number
  lastModified?: Date
  isFolder: boolean
  parentFolder: string | null
}

interface UseFilesProps {
  carbon: any
}

interface UseFilesReturn {
  // State
  files: FileInfo[]
  loading: boolean
  error: string | null
  currentFolder: string | null
  breadcrumbs: string[]
  folders: string[]
  searchTerm: string
  searchResults: FileInfo[]
  selectedFiles: Set<string>
  
  // Actions
  fetchFiles: () => Promise<void>
  downloadFile: (fileName: string) => Promise<void>
  uploadFile: (file: File) => Promise<void>
  uploadMultipleFiles: (files: FileList) => Promise<void>
  createFolder: (folderName: string) => Promise<void>
  deleteFile: (fileName: string) => Promise<void>
  deleteFolder: (folderName: string) => Promise<void>
  renameFile: (oldName: string, newName: string, folderPath: string) => Promise<void>
  renameFolder: (oldName: string, newName: string) => Promise<void>
  moveItem: (itemName: string, targetFolder: string) => Promise<string | null>
  downloadFolder: (folderPath: string) => Promise<void>
  downloadItems: (itemNames: string[]) => Promise<void>
  
  // Navigation
  setCurrentFolder: (folder: string | null) => void
  setBreadcrumbs: (breadcrumbs: string[]) => void
  handleFolderClick: (folderPath: string) => void
  handleBreadcrumbClick: (index: number) => void
  
  // Search
  handleSearch: (term: string) => void
  
  // File selection
  handleSelectFile: (fileName: string) => void
  handleSelectAll: (selected: boolean) => void
  clearSelection: () => void
  isAllSelected: () => boolean
  isIndeterminate: () => boolean
  
  // Utility
  getCurrentFolderContents: () => FileInfo[]
  sanitizeFileName: (fileName: string) => string
}

const sanitizeFileName = (fileName: string): string => {
  return fileName
    .trim()
    .replace(/^(\/|Docs\/)+/, '')
    .replace(/[<>:"|\\?*]/g, '_')  // Removed forward slash from the regex - it's a valid path separator
}

/** The folder path under Docs/ of a listing name: "Docs/A/B/" → "A/B". */
export const folderPathOf = (name: string): string =>
  name.replace(/^\/+/, '').replace(/^Docs\//, '').replace(/\/+$/, '')

const readBlobText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(blob)
      })

/** The reason a Files request failed, from the server's `error` (and
 *  `details`) when it sent one. A download asks for a blob, so its error body
 *  arrives as a Blob holding the JSON. */
export const requestErrorMessage = async (err: unknown, fallback: string): Promise<string> => {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  let body: unknown = data
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      body = JSON.parse(await readBlobText(data))
    } catch {
      body = undefined
    }
  }
  if (body && typeof body === 'object') {
    const { error, details } = body as { error?: unknown; details?: unknown }
    if (typeof error === 'string' && error) {
      return typeof details === 'string' && details ? `${error}: ${details}` : error
    }
  }
  return err instanceof Error && err.message ? `${fallback}: ${err.message}` : fallback
}

/** Saves a downloaded blob under fileName through a temporary link. */
const saveBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

const getHeaders = (): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
}

const listFilesRequest = async (customer: string, job: string): Promise<any[]> => {
  const response = await axios.get('/api/v1/files/list', {
    params: { customer, job },
    headers: getHeaders(),
  })
  return response.data
}

const downloadFileRequest = async (customer: string, job: string, fileName: string): Promise<Blob> => {
  const response = await axios.get('/api/v1/files/download', {
    params: { customer, job, fileName },
    headers: getHeaders(),
    responseType: 'blob'
  })
  return response.data
}

const uploadFileRequest = async (customer: string, job: string, file: File, folderName: string): Promise<any> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('customer', customer)
  formData.append('job', job)
  formData.append('folderName', folderName)

  const response = await axios.post('/api/v1/files/upload', formData, {
    headers: {
      ...getHeaders(),
      'Content-Type': 'multipart/form-data'
    },
  })
  return response.data
}

const createFolderRequest = async (customer: string, job: string, folderName: string): Promise<any> => {
  const response = await axios.post('/api/v1/files/folder/create', {
    customer,
    job,
    folderName
  }, {
    headers: getHeaders(),
  })
  return response.data
}

const deleteFileRequest = async (customer: string, job: string, fileName: string): Promise<any> => {
  const response = await axios.delete('/api/v1/files/file', {
    data: { customer, job, fileName },
    headers: getHeaders(),
  })
  return response.data
}

const deleteFolderRequest = async (customer: string, job: string, folderName: string): Promise<any> => {
  const response = await axios.delete('/api/v1/files/folder', {
    params: { customer, job, folderName },
    headers: getHeaders(),
  })
  return response.data
}

const renameFileRequest = async (customer: string, job: string, oldFileName: string, newFileName: string): Promise<any> => {
  const response = await axios.post('/api/v1/files/file/rename', {
    customer,
    job,
    oldFileName,
    newFileName
  }, {
    headers: getHeaders(),
  })
  return response.data
}

const renameFolderRequest = async (customer: string, job: string, oldFolderName: string, newFolderName: string): Promise<any> => {
  const response = await axios.post('/api/v1/files/folder/rename', {
    customer,
    job,
    oldFolderName,
    newFolderName
  }, {
    headers: getHeaders(),
  })
  return response.data
}

const moveFileRequest = async (customer: string, job: string, sourceFileName: string, targetFolderName: string): Promise<any> => {
  const response = await axios.post('/api/v1/files/file/move', {
    customer,
    job,
    sourceFileName,
    targetFolderName
  }, {
    headers: getHeaders(),
  })
  return response.data
}

const moveFolderRequest = async (customer: string, job: string, folderName: string, targetFolderName: string): Promise<any> => {
  const response = await axios.post('/api/v1/files/folder/move', {
    customer,
    job,
    folderName,
    targetFolderName
  }, {
    headers: getHeaders(),
  })
  return response.data
}

const downloadZipRequest = async (customer: string, job: string, paths: string[]): Promise<Blob> => {
  const response = await axios.post('/api/v1/files/zip', { customer, job, paths }, {
    headers: getHeaders(),
    responseType: 'blob'
  })
  return response.data
}

const downloadFolderRequest = async (customer: string, job: string, folderPath: string): Promise<Blob> => {
  const response = await axios.get('/api/v1/files/folder/download', {
    params: { customer, job, folderPath },
    headers: getHeaders(),
    responseType: 'blob'
  })
  return response.data
}

export const useFiles = (): UseFilesReturn => {
  const account = useAccount()
  const snackbar = useSnackbar()
  
  const [files, setFiles] = useState<FileInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentFolder, setCurrentFolder] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>(['Home'])
  const [folders, setFolders] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<FileInfo[]>([])
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set())

  const fetchFiles = useCallback(async () => {
    if (!account.customer || !account.job) {
      setFiles([])
      setSelectedFiles(new Set())
      return
    }

    setLoading(true)
    setError(null)

    try {
      const fileList = await listFilesRequest(account.customer?.name || '', account.job?.name || '')
      
      const processedFileList = fileList.map((file: any) => ({
        ...file,
        lastModified: file.lastModified ? new Date(file.lastModified) : undefined
      }))
      
      setFiles(processedFileList)
      
      // Every folder's full path, at any depth ("A", "A/B").
      const folderSet = new Set<string>(
        fileList
          .filter((file: any) => file.isFolder)
          .map((file: any) => folderPathOf(file.name))
      )
      setFolders(Array.from(folderSet).sort())
      setSelectedFiles(new Set())
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to load files')
      setError(errorMessage)
      setFiles([])
      setSelectedFiles(new Set())
    } finally {
      setLoading(false)
    }
  }, [account.customer, account.job])

  const downloadFile = useCallback(async (fileName: string) => {
    if (!account.customer || !account.job) return

    try {
      const sanitizedFileName = sanitizeFileName(fileName)
      const blob = await downloadFileRequest(account.customer?.name || '', account.job?.name || '', sanitizedFileName)
      saveBlob(blob, fileName.split('/').pop() || fileName)
    } catch (err) {
      snackbar.error(await requestErrorMessage(err, 'Failed to download file'))
    }
  }, [account.customer, account.job])

  const uploadFile = useCallback(async (file: File) => {
    if (!account.customer || !account.job) {
      snackbar.error('Please select a customer and job first')
      return
    }

    setLoading(true)
    
    try {
      const result = await uploadFileRequest(
        account.customer?.name || '',
        account.job?.name || '' ,
        file,
        currentFolder || ''
      )
      
      snackbar.success(`Successfully uploaded: ${result.fileName || file.name}`)
      await fetchFiles()
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to upload file')
      snackbar.error(`Error uploading ${file.name}: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }, [account.customer, account.job, currentFolder, fetchFiles])

  const uploadMultipleFiles = useCallback(async (files: FileList) => {
    if (!account.customer || !account.job) {
      snackbar.error('Please select a customer and job first')
      return
    }

    setLoading(true)
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      try {
        const result = await uploadFileRequest(
          account.customer?.name || '',
          account.job?.name || '',
          file,
          currentFolder || ''
        )
        
        snackbar.success(`Successfully uploaded: ${result.fileName || file.name}`)
      } catch (err) {
        const errorMessage = await requestErrorMessage(err, 'Failed to upload file')
        snackbar.error(`Error uploading ${file.name}: ${errorMessage}`)
      }
    }
    
    await fetchFiles()
    setLoading(false)
  }, [account.customer, account.job, currentFolder, fetchFiles])

  const createFolder = useCallback(async (folderName: string) => {
    if (!account.customer || !account.job || !folderName) return

    try {
      const path = currentFolder ? `${currentFolder}/${folderName}` : folderName
      await createFolderRequest(account.customer?.name || '', account.job?.name || '', path)
      await fetchFiles()
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to create folder')
      snackbar.error(errorMessage)
    }
  }, [account.customer, account.job, currentFolder, fetchFiles])

  const deleteFile = useCallback(async (fileName: string) => {
    if (!account.customer || !account.job) return

    try {
      const fullPath = fileName.startsWith('Docs/') ? fileName : `Docs/${fileName}`
      await deleteFileRequest(account.customer?.name || '', account.job?.name || '', fullPath)
      await fetchFiles()
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to delete file')
      snackbar.error(errorMessage)
    }
  }, [account.customer, account.job, fetchFiles])

  const deleteFolder = useCallback(async (folderName: string) => {
    if (!account.customer || !account.job) return

    try {
      await deleteFolderRequest(account.customer?.name || '', account.job?.name || '', folderName)
      await fetchFiles()
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to delete folder')
      snackbar.error(errorMessage)
    }
  }, [account.customer, account.job, fetchFiles])

  const renameFile = useCallback(async (oldName: string, newName: string, folderPath: string) => {
    if (!account.customer || !account.job) return

    try {
      const newPath = `${folderPath}/${newName}`
      await renameFileRequest(account.customer?.name || '', account.job?.name || '', oldName, newPath)
      await fetchFiles()
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to rename file')
      snackbar.error(errorMessage)
    }
  }, [account.customer, account.job, fetchFiles])

  const renameFolder = useCallback(async (oldName: string, newName: string) => {
    if (!account.customer || !account.job) return

    try {
      // oldName is the folder's full path; the new name replaces its last segment.
      const parent = oldName.includes('/') ? oldName.slice(0, oldName.lastIndexOf('/') + 1) : ''
      await renameFolderRequest(account.customer?.name || '', account.job?.name || '', oldName, `${parent}${newName}`)
      await fetchFiles()
    } catch (err) {
      const errorMessage = await requestErrorMessage(err, 'Failed to rename folder')
      snackbar.error(errorMessage)
    }
  }, [account.customer, account.job, fetchFiles])

  /** Moves a file, or a folder (a name ending in "/") with everything in it,
   *  into targetFolder ("" is the top level). Returns null when it moved, or
   *  the reason it did not. The caller refreshes the list and tells the user. */
  const moveItem = useCallback(async (itemName: string, targetFolder: string): Promise<string | null> => {
    if (!account.customer || !account.job) return 'Choose a customer and job first'

    const customer = account.customer?.name || ''
    const job = account.job?.name || ''
    try {
      if (itemName.endsWith('/')) {
        await moveFolderRequest(customer, job, itemName, targetFolder)
      } else {
        await moveFileRequest(customer, job, itemName, targetFolder)
      }
      return null
    } catch (err) {
      return requestErrorMessage(err, itemName.endsWith('/') ? 'Failed to move folder' : 'Failed to move file')
    }
  }, [account.customer, account.job])

  const downloadFolder = useCallback(async (folderPath: string) => {
    if (!account.customer || !account.job) return

    const name = folderPathOf(folderPath).split('/').pop() || 'folder'
    snackbar.info(`Preparing ${name}.zip…`)
    try {
      const sanitizedFolderPath = sanitizeFileName(folderPath)
      const blob = await downloadFolderRequest(account.customer?.name || '', account.job?.name || '', sanitizedFolderPath)
      saveBlob(blob, `${name}.zip`)
      snackbar.reset()
    } catch (err) {
      snackbar.error(await requestErrorMessage(err, 'Failed to download folder'))
    }
  }, [account.customer, account.job])

  /** The header Download: one file downloads as itself, one folder as its
   *  zip, and anything more as one zip holding every ticked item. */
  const downloadItems = useCallback(async (itemNames: string[]) => {
    if (itemNames.length === 0) return
    if (itemNames.length === 1) {
      const [only] = itemNames
      if (only.endsWith('/')) await downloadFolder(only)
      else await downloadFile(only.replace(/^Docs\//, ''))
      return
    }
    if (!account.customer || !account.job) return

    const job = account.job?.name || ''
    snackbar.info(`Preparing a zip of ${itemNames.length} items…`)
    try {
      const blob = await downloadZipRequest(account.customer?.name || '', job, itemNames)
      saveBlob(blob, `${job} files.zip`)
      snackbar.reset()
    } catch (err) {
      snackbar.error(await requestErrorMessage(err, 'Failed to download the files'))
    }
  }, [account.customer, account.job, downloadFile, downloadFolder])

  /** Opens a folder by its full path under Docs/ ("A/B"). */
  const handleFolderClick = useCallback((folderPath: string) => {
    const path = folderPathOf(folderPath)
    setCurrentFolder(path || null)
    setBreadcrumbs(['Home', ...(path ? path.split('/') : [])])
  }, [])

  const handleBreadcrumbClick = useCallback((index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1)
    setBreadcrumbs(newBreadcrumbs)
    const path = newBreadcrumbs.slice(1).join('/')
    setCurrentFolder(path || null)
  }, [breadcrumbs])

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term)
    if (term.trim() === '') {
      setSearchResults([])
      return
    }
    const results = files.filter(file => 
      file.displayName.toLowerCase().includes(term.toLowerCase())
    )
    setSearchResults(results)
  }, [files])

  const getCurrentFolderContents = useCallback(() => {
    if (searchTerm.trim() !== '') {
      return searchResults.sort((a, b) => {
        // Sort folders first, then files
        if (a.isFolder && !b.isFolder) return -1
        if (!a.isFolder && b.isFolder) return 1
        return a.displayName.localeCompare(b.displayName)
      })
    }
    
    const filteredFiles = files.filter((file: FileInfo) => 
      currentFolder === null 
        ? file.parentFolder === null 
        : file.parentFolder === currentFolder
    )
    
    return filteredFiles.sort((a, b) => {
      // Sort folders first, then files
      if (a.isFolder && !b.isFolder) return -1
      if (!a.isFolder && b.isFolder) return 1
      return a.displayName.localeCompare(b.displayName)
    })
  }, [searchTerm, searchResults, files, currentFolder])

  const handleSelectFile = useCallback((fileName: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev)
      if (newSet.has(fileName)) {
        newSet.delete(fileName)
      } else {
        newSet.add(fileName)
      }
      return newSet
    })
  }, [])

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      const visibleFileNames = getCurrentFolderContents().map(file => file.name)
      setSelectedFiles(new Set(visibleFileNames))
    } else {
      setSelectedFiles(new Set())
    }
  }, [getCurrentFolderContents])

  const clearSelection = useCallback(() => {
    setSelectedFiles(new Set())
  }, [])

  const isAllSelected = useCallback(() => {
    const visibleFiles = getCurrentFolderContents()
    return visibleFiles.length > 0 && 
           selectedFiles.size === visibleFiles.length &&
           visibleFiles.every(file => selectedFiles.has(file.name))
  }, [getCurrentFolderContents, selectedFiles])

  const isIndeterminate = useCallback(() => {
    const visibleFiles = getCurrentFolderContents()
    return selectedFiles.size > 0 && 
           selectedFiles.size < visibleFiles.length && 
           visibleFiles.some(file => selectedFiles.has(file.name))
  }, [getCurrentFolderContents, selectedFiles])

  // Auto-fetch files when account changes
  useEffect(() => {
    if (account.customer && account.job) {
      fetchFiles()
    }
  }, [account.customer, account.job])

  return {
    // State
    files,
    loading,
    error,
    currentFolder,
    breadcrumbs,
    folders,
    searchTerm,
    searchResults,
    selectedFiles,
    
    // Actions
    fetchFiles,
    downloadFile,
    uploadFile,
    uploadMultipleFiles,
    createFolder,
    deleteFile,
    deleteFolder,
    renameFile,
    renameFolder,
    moveItem,
    downloadFolder,
    downloadItems,
    
    // Navigation
    setCurrentFolder,
    setBreadcrumbs,
    handleFolderClick,
    handleBreadcrumbClick,
    
    // Search
    handleSearch,
    
    // File selection
    handleSelectFile,
    handleSelectAll,
    clearSelection,
    isAllSelected,
    isIndeterminate,
    
    // Utility
    getCurrentFolderContents,
    sanitizeFileName
  }
}

export default useFiles